import type { ReactNode } from 'react';

type BadgeVariant =
  | 'default'
  | 'success'
  | 'warning'
  | 'danger'
  | 'info'
  | 'navy'
  | 'red'
  | 'orange';

type BadgeSize = 'sm' | 'md' | 'lg';

interface BadgeProps {
  children: ReactNode;
  variant?: BadgeVariant;
  size?: BadgeSize;
  className?: string;
  icon?: ReactNode;
}

const variantClasses: Record<BadgeVariant, string> = {
  default: 'bg-gray-100 text-gray-700',
  success: 'bg-green-100 text-green-700',
  warning: 'bg-amber-100 text-amber-700',
  danger: 'bg-red-100 text-red-700',
  info: 'bg-blue-100 text-blue-700',
  navy: 'bg-hul-navy text-white',
  red: 'bg-hul-red text-white',
  orange: 'bg-hul-orange text-white',
};

const sizeClasses: Record<BadgeSize, string> = {
  sm: 'px-2 py-0.5 text-xs',
  md: 'px-3 py-1 text-sm',
  lg: 'px-4 py-1.5 text-base',
};

export function Badge({
  children,
  variant = 'default',
  size = 'md',
  className = '',
  icon,
}: BadgeProps) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 font-medium rounded-full ${variantClasses[variant]} ${sizeClasses[size]} ${className}`}
    >
      {icon}
      {children}
    </span>
  );
}

// Specialized badges for common use cases

interface ConditionBadgeProps {
  condition: 'Excellent' | 'Good' | 'Fair' | 'Poor' | 'New' | 'Used' | 'Certified Pre-Owned' |
             'excellent' | 'good' | 'fair' | 'poor' | 'new' | 'used' | 'certified pre-owned' | string;
}

export function ConditionBadge({ condition }: ConditionBadgeProps) {
  const getVariant = (): BadgeVariant => {
    const normalizedCondition = condition.toLowerCase();
    switch (normalizedCondition) {
      case 'excellent':
      case 'new':
        return 'success';
      case 'good':
      case 'certified pre-owned':
        return 'info';
      case 'fair':
      case 'used':
        return 'warning';
      case 'poor':
        return 'danger';
      default:
        return 'default';
    }
  };

  // Capitalize first letter for display
  const displayCondition = condition.charAt(0).toUpperCase() + condition.slice(1);
  return <Badge variant={getVariant()}>{displayCondition}</Badge>;
}

interface SourceBadgeProps {
  source: 'AI_EXTRACTION' | 'INVENTORY' | 'MANUAL_ENTRY';
}

export function SourceBadge({ source }: SourceBadgeProps) {
  const labels: Record<string, string> = {
    AI_EXTRACTION: 'AI Extracted',
    INVENTORY: 'From Inventory',
    MANUAL_ENTRY: 'Custom Config',
  };

  const variants: Record<string, BadgeVariant> = {
    AI_EXTRACTION: 'info',
    INVENTORY: 'navy',
    MANUAL_ENTRY: 'default',
  };

  return (
    <Badge variant={variants[source]} size="sm">
      {labels[source]}
    </Badge>
  );
}

interface FuelTypeBadgeProps {
  fuelType: string;
}

export function FuelTypeBadge({ fuelType }: FuelTypeBadgeProps) {
  const getVariant = (): BadgeVariant => {
    if (fuelType.toLowerCase().includes('electric')) return 'success';
    if (fuelType.toLowerCase().includes('lp') || fuelType.toLowerCase().includes('gas')) return 'orange';
    if (fuelType.toLowerCase().includes('diesel')) return 'navy';
    return 'default';
  };

  return <Badge variant={getVariant()}>{fuelType}</Badge>;
}

interface QuantityBadgeProps {
  quantity: number;
}

export function QuantityBadge({ quantity }: QuantityBadgeProps) {
  if (quantity <= 1) return null;

  return (
    <Badge variant="red" size="lg" className="font-heading">
      ×{quantity}
    </Badge>
  );
}
